import { locales } from "~/locales";
import { useFlagWithReady } from "~/composables/useFlag";

export const useCoreStore = defineStore("coreStore", () => {
  const { flag: showBossBattle, ready: bossBattleReady } =
    useFlagWithReady("boss_battle");
  const { flag: showExchanger, ready: exchangerReady } =
    useFlagWithReady("exchanger_event");
  const refreshMutex = ref<Promise<void>>();
  const vk = ref("https://vk.com/bulldrop");
  const inst = ref("https://vk.com/bulldrop");

  const cdn = computed(() => {
    if (useRequestURL().hostname === "bulldrop.vip") {
      return `https://bulldrop.vip/cdn-cgi/image`;
    } else if (useRequestURL().hostname === "bulldrop.uz") {
      return `https://bulldrop.uz/cdn-cgi/image`;
    } else {
      return `https://dev.bulltrade.org/cdn-cgi/image`;
    }
  });

  const tg = computed(() => {
    const platform = usePlatform();
    if (platform.is.pubg) return "https://t.me/bulldrop_pubg";
    if (platform.is.cs2) return "https://t.me/bulldrop_cs2";
    if (platform.is.genshin) return "https://t.me/bulldrop_genshin";
    if (platform.is.roblox) return "https://t.me/bulldroproblox";
    if (platform.is.bgmi) return "https://t.me/bulldrop_bgmi";
    return "https://t.me/bulldrop_standoff2";
  });
  const tgSupportBot = ref("https://t.me/BulldropSupportBot");
  const discord = ref("https://discord.gg/bulldrop");
  const gamelight = ref("https://gamelight.ru");
  const gamelightPubg = ref("https://gamelight.ru/products/15");
  const steamUrl = ref("https://gamelight.ru/products/19");
  const imageSrcsetExceptions = ["DragonEgg.webp"];
  const percent = ref<number | null>(null);
  const topUpBalanceFormIsRequired = ref(false);
  const withdrawFormIsRequired = ref(false);
  const headerMenuIsRequired = ref(false);
  const languageSubmenuIsRequired = ref(false);
  const platformSwitcherIsRequired = ref(false);
  const topUpInitialSum = ref(101);
  const audioIsRequired = ref(true);
  const userCountry = ref<string | null>(null);
  const isUserIndia = computed(() => userCountry && userCountry.value === "IN");
  const isUserLangSpanish = computed(() => {
    // список кодов испаноязычных стран Латинской Америки
    const spanishCountries = [
      "ar",
      "bo",
      "ve",
      "gt",
      "hn",
      "do",
      "co",
      "cr",
      "cu",
      "mx",
      "ni",
      "pa",
      "py",
      "pe",
      "sv",
      "uy",
      "cl",
      "ec",
      "sp",
      "es",
    ];
    return userCountry && userCountry.value
      ? spanishCountries.includes(userCountry.value.toLowerCase())
      : false;
  });

  const isUserLangPortugal = computed(() => {
    // список кодов португалоязычных стран Латинской Америки
    const portugalCountries = ["pt", "br"];
    return userCountry && userCountry.value
      ? portugalCountries.includes(userCountry.value.toLowerCase())
      : false;
  });

  const isUserFromSNG = computed(() => {
    // список кодов СНГ-стран
    const sngCountries = [
      "az",
      "am",
      "by",
      "kz",
      "kg",
      "md",
      "ru",
      "tj",
      "uz",
      "tm",
      "ge",
      "ua",
    ];
    return userCountry && userCountry.value
      ? sngCountries.includes(userCountry.value.toLowerCase())
      : false;
  });

  const isUserFromMENA = computed(() => {
    // список кодов СНГ-стран
    const menaCountries = [
      "af",
      "dz",
      "bh",
      "cy",
      "dj",
      "eg",
      "ir",
      "iq",
      "il",
      "jo",
      "kw",
      "lb",
      "ly",
      "mt",
      "mr",
      "ma",
      "om",
      "pk",
      "ps",
      "qa",
      "sa",
      "so",
      "sd",
      "sy",
      "tn",
      "tr",
      "ae",
      "ye",
    ];
    return userCountry && userCountry.value
      ? menaCountries.includes(userCountry.value.toLowerCase())
      : false;
  });

  const isUserLatin = computed(() => {
    return isUserLangSpanish.value || isUserLangPortugal.value;
  });

  const isUserBrasil = computed(() => {
    return userCountry && userCountry.value
      ? userCountry.value.toLowerCase() === "br"
      : false;
  });

  function checkIsLocaleEqualUserCountry() {
    const handleLang = localStorage.getItem("handleLocale");

    if (handleLang) {
      return handleLang;
    }

    if (userCountry && userCountry.value) {
      if (isUserLangSpanish.value) {
        return "es";
      } else if (isUserLangPortugal.value) {
        return "pt";
      } else {
        const code = userCountry.value.toLowerCase();
        const found = locales.find((locale) => locale.code === code);
        return found ? found.code : "en";
      }
    } else return "en";
  }

  const showCs2SpecificContent = computed(() => {
    const platform = usePlatform();
    return platform.is.cs2 && !isUserIndia.value;
  });

  const intersectionObserverIsSupported = computed(() => {
    if ("IntersectionObserver" in window) {
      return true;
    } else {
      return false;
    }
  });
  const gameStats = ref({
    players: 0,
    casesOpened: 0,
    ladderBets: 0,
    crashBets: 0,
    minerBets: 0,
    wheelBets: 0,
  });
  const gameStatsFetched = ref(false);

  async function getGameStats() {
    if (gameStatsFetched.value) return;
    const api = useApi();
    try {
      const data = await api.games.overviewFooterValuesRetrieve();
      gameStats.value = data;
      gameStatsFetched.value = true;
    } catch (error) {
      /* error */
    }
  }

  function toggleAudio() {
    audioIsRequired.value = !audioIsRequired.value;
    useCookie("audioIsRequired").value = JSON.stringify(audioIsRequired.value);
  }

  function showMenuHeader() {
    headerMenuIsRequired.value = true;
    languageSubmenuIsRequired.value = false;
  }

  function hideMenuHeader() {
    headerMenuIsRequired.value = false;
    document.getElementById("layout")!.style.removeProperty("overflow");
  }

  function toggleLanguageSubmenu() {
    languageSubmenuIsRequired.value = !languageSubmenuIsRequired.value;
  }

  function showLanguageSubmenu() {
    languageSubmenuIsRequired.value = true;
  }

  function hideLanguageSubmenu() {
    languageSubmenuIsRequired.value = false;
  }

  function showPlatformSwitcher() {
    platformSwitcherIsRequired.value = true;
  }

  function hidePlatfromSwitcher() {
    platformSwitcherIsRequired.value = false;
  }

  function togglePlatformSwitcher() {
    platformSwitcherIsRequired.value = !platformSwitcherIsRequired.value;
  }

  function showLanguageSubmenuMobile() {
    headerMenuIsRequired.value = false;
    languageSubmenuIsRequired.value = true;
  }

  function toggleMenuHeader() {
    headerMenuIsRequired.value = !headerMenuIsRequired.value;
    if (headerMenuIsRequired.value) {
      document.getElementById("layout")!.style.overflow = "hidden";
    } else {
      document.getElementById("layout")!.style.removeProperty("overflow");
    }
  }

  function showTopUpBalanceForm(initialSum: number = 0) {
    document.documentElement.style.overflow = "hidden";
    topUpBalanceFormIsRequired.value = true;
    topUpInitialSum.value = initialSum;
  }

  function hideTopUpBalanceForm() {
    topUpBalanceFormIsRequired.value = false;
    topUpInitialSum.value = 0;
    document.documentElement.style.overflow = "";
  }

  function showWithdrawForm() {
    document.documentElement.style.overflow = "hidden";
    withdrawFormIsRequired.value = true;
  }

  function hideWithdrawForm() {
    withdrawFormIsRequired.value = false;
    document.documentElement.style.overflow = "";
  }

  return {
    showBossBattle,
    showExchanger,
    bossBattleReady,
    exchangerReady,
    refreshMutex,
    vk,
    cdn,
    tg,
    inst,
    tgSupportBot,
    discord,
    gamelight,
    gamelightPubg,
    steamUrl,
    percent,
    topUpBalanceFormIsRequired,
    withdrawFormIsRequired,
    headerMenuIsRequired,
    platformSwitcherIsRequired,
    languageSubmenuIsRequired,
    topUpInitialSum,
    audioIsRequired,
    intersectionObserverIsSupported,
    imageSrcsetExceptions,
    gameStats,
    userCountry,
    isUserIndia,
    isUserLangSpanish,
    isUserLangPortugal,
    isUserLatin,
    isUserBrasil,
    isUserFromSNG,
    isUserFromMENA,
    showCs2SpecificContent,
    getGameStats,
    toggleAudio,
    showMenuHeader,
    hideMenuHeader,
    toggleLanguageSubmenu,
    showLanguageSubmenu,
    hideLanguageSubmenu,
    showPlatformSwitcher,
    hidePlatfromSwitcher,
    togglePlatformSwitcher,
    showLanguageSubmenuMobile,
    toggleMenuHeader,
    showTopUpBalanceForm,
    hideTopUpBalanceForm,
    showWithdrawForm,
    hideWithdrawForm,
    checkIsLocaleEqualUserCountry,
  };
});
